import { useRouter, useRuntimeConfig } from '#imports';
import { minimatch } from 'minimatch';
import { setup } from '~/utils/meta/meta-pixel';
import type { Pixel } from '../modules/meta-pixel/types';
import { useInitialOnce } from './useInitialOnce';

type MetaPixelState = {
    debug: boolean
    pixels: Record<number, Pixel>
    lastPath: string | null
    consentGranted: boolean
}

export function useMetaPixel() {
    if (!import.meta.client) return {} as any

    const router = useRouter()
    const runtimeConfig = useRuntimeConfig()
    const {
        $fbq,
        init,
        pageView
    } = setup();

    const state = reactive<MetaPixelState>({
        debug: false,
        pixels: {},
        lastPath: null,
        consentGranted: false
    }) as MetaPixelState;

    function log(...args: any[]) {
        if (state.debug) {
            console.log('[Meta-Pixel]', ...args)
        }
    }

    async function loadConfig() {
        const api = useInitialOnce('meta') ?? await useApi('/setting/meta');

        const cfg = {
            ...runtimeConfig.public.metapixel,
            ...api
        }

        state.debug = cfg.debug;
        state.pixels = cfg.pixels ?? {};

        log('Config loaded:', cfg);

        return cfg;
    }

    function initPixels() {
        for (const p of Object.values(state.pixels)) {
            init(p.id, p.autoconfig, p.consent);
        }

        log('Pixels initialized.');
    }

    function trackPageView(path: string) {
        if (state.lastPath === path) return;

        state.lastPath = path;

        for (const p of Object.values(state.pixels)) {
            const patterns = Array.isArray(p.pageView)
                ? p.pageView
                : [p.pageView ?? '**']

            if (patterns.some(m => minimatch(path, m))) {
                log('PageView', p.id, path)
                pageView(p.id);
            }
        }
    }

    function enableAutoTracking() {
        router.afterEach((to: any, _: any, failure: any) => {
            if (failure) return;

            trackPageView(to.fullPath);
        })
    }

    return {
        $fbq,
        loadConfig,
        initPixels,
        trackPageView,
        enableAutoTracking,
    }
}
